import './calendario-lavori.scss';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from '../../themes/theme';
import { useUserSessionContext } from '../../contexts/UserSessionContext';
import { useScreenSizeClass } from '../../utils/media-query';
import { Button, LoadPanel, Scheduler, SelectBox } from 'devextreme-react';
import { View } from 'devextreme-react/scheduler';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { LavoriPopupEdit, LavoriPopupLista } from '../../components';
import { PopupListaHandle } from '../../components/popup-lavori/popupListaLavori';
import { PopupEditHandle } from '../../components/popup-lavori/popupEdit';
import { CalendarioLavoriResponseDTO, LavoriApi, PersonaleApi } from '../../data';
import useApiConfiguration from '../../apiConfig';

export default function CalendarioLavori() {
    const themeContext = useContext(ThemeContext);
    const isDarkMode = themeContext?.theme === "dark";

    const popupEditRef = useRef<PopupEditHandle>(null);
    const popupListaRef = useRef<PopupListaHandle>(null);

    const apiConfiguration = useApiConfiguration();
    const { user } = useUserSessionContext();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState(false);
    const [personaleId, setPersonaleId] = useState(user.id);
    const [dataSourcePersonale, setDataSourcePersonale] = useState<any[]>([]);
    const [canChangePersonale, setChangePersonale] = useState(false);

    const [dataSource, setDataSource] = useState<any[]>([]);
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [dataInizio, setDataInizio] = useState<Date>(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    const [dataFine, setDataFine] = useState<Date>(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));

    const [giorniMap, setGiorniMap] = useState<Map<string, CalendarioLavoriResponseDTO>>(new Map());

    function refreshData() {
        const api = new LavoriApi(apiConfiguration);
        const off = dataInizio.getTimezoneOffset();
        const dataInizioTimezone = new Date(dataInizio.getTime() - off * 60 * 1000);

        api.getCalendarioLavori({
            idPersonale: personaleId,
            data: dataInizioTimezone
        }).then((response) => {
            const map = new Map<string, CalendarioLavoriResponseDTO>();
            response.forEach((g: CalendarioLavoriResponseDTO) => {
                const key = g.data.toISOString().slice(0, 10);
                map.set(key, g);
            });
            setGiorniMap(map);

            const appts = response
                .filter((g: CalendarioLavoriResponseDTO) => (g.ore ?? 0) > 0)
                .map((g: CalendarioLavoriResponseDTO) => {
                    const start = new Date(g.data.getFullYear(), g.data.getMonth(), g.data.getDate(), 6, 0, 0);
                    const end = new Date(g.data.getFullYear(), g.data.getMonth(), g.data.getDate(), 22, 0, 0);

                    return {
                        startDate: start,
                        endDate: end,
                        text: `${g.ore}`,
                        anomalia: g.anomalia ?? false,
                        festivo: g.festivo,
                        ore: g.ore,
                        raw: g,
                        allowAdd: g.allowAdd ?? false
                    };
                });

            setDataSource(appts);
        }).catch((error) => {
            console.error(error);
        }).finally(() => setLoading(false))
    };

    function changeDipendente(e: any) {
        setPersonaleId(e.value);
    };

    useEffect(() => {
        const api = new PersonaleApi(apiConfiguration);
        api.getPersonale({ date: dataFine }).then((res) => {
            setDataSourcePersonale(res);
            setChangePersonale(res.length > 1);
        });
    }, [dataFine]);

    useEffect(() => {
        setLoading(true);
        refreshData();
    }, [dataFine, personaleId]);

    function onCurrentDateChange(e: Date) {
        setDataInizio(new Date(e.getFullYear(), e.getMonth(), 1));
        setDataFine(new Date(e.getFullYear(), e.getMonth() + 1, 0));
    };

    const DataCell = (cell: any) => {
        const startDate: Date = cell.startDate;

        // mostra solo i giorni del mese corrente
        const isSameMonth =
            startDate.getMonth() === dataInizio.getMonth() &&
            startDate.getFullYear() === dataInizio.getFullYear();
        if (!isSameMonth) {
            return <div className="tsm-cell tsm-cell--outside" />;
        }

        const key = startDate.toISOString().slice(0, 10);
        const giorno = giorniMap.get(key);
        const isFestivo = !!giorno?.festivo;
        // const ore = giorno?.ore ?? 0;
        // const mostraPlus = ore < 10 && !isFestivo;
        const mostraPlus = !!giorno?.allowAdd;

        const stopAll = (e: any) => {
            e.stopPropagation();
            e.preventDefault();
        }

        return (
            <div className={`tsm-cell ${isFestivo ? 'festivo' : ''}`}>
                <div className='tsm-head'>
                    <span className='tsm-day'>
                        {startDate.getDate()}
                    </span>
                </div>

                {mostraPlus && (
                    <div className='tsm-plus-br' onPointerDown={stopAll} onMouseDown={stopAll} onClick={stopAll}>
                        <Button
                            icon="add"
                            stylingMode='contained'
                            className={`tsm-plus-button ${isDarkMode ? 'darkmode' : ''}`}
                            onClick={(e) => {
                                e.event.stopPropagation();
                                e.event.preventDefault();

                                popupEditRef.current?.new(personaleId, startDate);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <React.Fragment>
            {
                loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' position={'center'} visible={true} />
            }

            <div className={`calendario-page view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                <Toolbar className='calendario-lavori-toolbar'>
                    <Item location='before'>
                        <div className="grid-header">Calendario</div>
                    </Item>

                    <Item
                        location="center"
                        locateInMenu="auto"
                        showText="inMenu"
                        widget="dxSelectBox"
                        visible={canChangePersonale}
                    >
                        <SelectBox className="dx-dipendente"
                            label="Dipendente" labelMode="floating"
                            dataSource={dataSourcePersonale}
                            displayExpr="descrizione"
                            valueExpr="idPersonale"
                            searchEnabled={true}
                            value={personaleId}
                            onValueChanged={changeDipendente}
                        />
                    </Item>
                </Toolbar>

                <Scheduler
                    dataSource={dataSource}
                    editing={false}
                    height={"auto"}
                    firstDayOfWeek={1}
                    activeStateEnabled={false}
                    dataCellRender={DataCell}
                    onAppointmentClick={(e) => {
                        e.cancel = true;
                        e.event.stopPropagation();
                        e.event.preventDefault();

                        var startDate = e.targetedAppointmentData.startDate;
                        var asDate: Date | undefined = typeof startDate === "string" ? new Date(startDate) : startDate;

                        popupListaRef.current?.show(personaleId, asDate);
                    }}
                    onAppointmentDblClick={(e) => { e.cancel = true; }}
                    appointmentComponent={(props) => {
                        return (
                            <div className={`dx-scheduler-appointment-title ${props.data.targetedAppointmentData.anomalia ? 'anomalia' : ''}`}>
                                {props.data.targetedAppointmentData.text}
                            </div>
                        )
                    }}
                    defaultCurrentView='Mese'
                    onCurrentDateChange={onCurrentDateChange}
                >
                    <View
                        firstDayOfWeek={1}
                        type='month'
                        name='Mese'
                        intervalCount={1}
                    />
                </Scheduler>
            </div>

            <LavoriPopupEdit ref={popupEditRef} onChange={refreshData} />
            <LavoriPopupLista ref={popupListaRef} onHide={refreshData} />
        </React.Fragment>
    );
};